import React from 'react'
import { Box, Chip, Typography } from '@mui/material'
import { LocalOffer as TagIcon, Check as CheckIcon } from '@mui/icons-material'
import { EntityModal } from './EntityModal'

interface TagMiniItem { id: string; name: string; color: string }

interface TagModalProps {
  open: boolean
  onClose: () => void
  allTags: TagMiniItem[]
  attachedTagIds: Set<string>
  onAdd: (tagId: string) => void
  isAdding: boolean
}

export const TagModal: React.FC<TagModalProps> = ({ open, onClose, allTags, attachedTagIds, onAdd, isAdding }) => {
  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Add Tag"
      icon={<TagIcon sx={{ fontSize: 17 }} />}
    >
      {allTags.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <TagIcon sx={{ fontSize: 36, color: '#e5e7eb', mb: 1 }} />
          <Typography fontSize="13px" color="text.secondary">
            No tags available. Create tags from the Tags page.
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography fontSize="12.5px" color="text.secondary" fontWeight={500} mb={2}>
            Tag Name
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {allTags.map(tag => {
              const isAttached = attachedTagIds.has(tag.id)
              return (
                <Chip
                  key={tag.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isAttached && <CheckIcon sx={{ fontSize: 12 }} />}
                      <span>{tag.name}</span>
                    </Box>
                  }
                  disabled={isAttached || isAdding}
                  onClick={() => !isAttached && !isAdding && onAdd(tag.id)}
                  sx={{
                    height: 32, fontSize: '12.5px', fontWeight: 600,
                    px: 0.5,
                    bgcolor: isAttached ? `${tag.color}15` : `${tag.color}18`,
                    color: isAttached ? `${tag.color}90` : tag.color,
                    border: `1.5px solid ${isAttached ? tag.color + '35' : tag.color + '55'}`,
                    cursor: isAttached ? 'default' : 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': !isAttached && !isAdding ? {
                      bgcolor: `${tag.color}30`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 3px 10px ${tag.color}30`,
                    } : {},
                    '&.Mui-disabled': {
                      opacity: 1,                         // keep colour visible
                      bgcolor: `${tag.color}10`,
                      color: `${tag.color}70`,
                      border: `1.5px solid ${tag.color}25`,
                    },
                  }}
                />
              )
            })}
          </Box>
        </Box>
      )}
    </EntityModal>
  )
}
